import { createFileRoute } from '@tanstack/react-router'
import { useQuery, useMutation, createConnectQueryKey } from '@connectrpc/connect-query'
import { useQueryClient } from '@tanstack/react-query'
import { useMemo, useState } from 'react'
import { toast } from 'sonner'
import {
  adminListFeatureCatalog,
  adminDisableFeatureForAllOrgs,
} from '../../gen/cortado/v1/cortado-CortadoService_connectquery'
import { OrganizationFeatureEnum, OrganizationFeatureStageEnum } from '../../gen/cortado/v1/settings_pb'
import { deadFeatureRows, featureLabel } from '../../lib/feature-catalog'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table'
import { Badge } from '../../components/ui/badge'
import { Button } from '../../components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../../components/ui/dialog'
import { ShieldCheck } from 'lucide-react'

function stageLabel(stage: OrganizationFeatureStageEnum): string {
  return stage === OrganizationFeatureStageEnum.ORGANIZATION_FEATURE_STAGE_RETIRED ? 'Retired' : 'GA'
}

export const Route = createFileRoute('/_admin/feature-hygiene')({
  component: FeatureHygienePage,
})

function FeatureHygienePage() {
  const queryClient = useQueryClient()
  const [revokeTarget, setRevokeTarget] = useState<{
    feature: OrganizationFeatureEnum
    enabledOrgCount: number
  } | null>(null)

  const { data: catalogData, isLoading, isError } = useQuery(adminListFeatureCatalog, {})

  const rows = useMemo(() => deadFeatureRows(catalogData?.entries ?? []), [catalogData?.entries])

  const invalidateCatalog = () => {
    queryClient.invalidateQueries({
      queryKey: createConnectQueryKey({
        schema: adminListFeatureCatalog,
        cardinality: undefined,
      }),
    })
  }

  const disableAllMutation = useMutation(adminDisableFeatureForAllOrgs, {
    onSuccess: (response) => {
      toast.success(`Disabled ${response.disabledCount} organization row${response.disabledCount === 1 ? '' : 's'}`)
      setRevokeTarget(null)
      invalidateCatalog()
    },
    onError: (err: Error) => {
      toast.error(err.message || 'Failed to disable feature for all organizations')
    },
  })

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Feature Hygiene</h1>
        <p className="text-muted-foreground text-sm">
          GA and Retired flags no longer gate any code path. Organizations with a leftover enabled
          row are listed here so they can be cleared in bulk.
        </p>
      </div>

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Feature</TableHead>
              <TableHead>Stage</TableHead>
              <TableHead>Enabled Orgs</TableHead>
              <TableHead className="w-32 text-right">Action</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading || isError ? (
              <TableRow>
                <TableCell colSpan={4} className="text-center text-muted-foreground py-8">
                  {isError ? 'Failed to load feature catalog' : 'Loading...'}
                </TableCell>
              </TableRow>
            ) : !rows.length ? (
              <TableRow>
                <TableCell colSpan={4} className="py-8">
                  <div className="flex flex-col items-center gap-2 text-center">
                    <ShieldCheck className="h-6 w-6 text-green-600 dark:text-green-400" />
                    <p className="font-medium">All clear</p>
                    <p className="text-muted-foreground text-sm">
                      No GA or Retired flag has an enabled org row — the data matches the code.
                    </p>
                  </div>
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row) => (
                <TableRow key={row.feature}>
                  <TableCell className="font-medium">{featureLabel(row.feature)}</TableCell>
                  <TableCell>
                    <Badge variant="secondary">{stageLabel(row.stage)}</Badge>
                  </TableCell>
                  <TableCell>{row.enabledOrgCount}</TableCell>
                  <TableCell className="text-right">
                    <Button
                      size="sm"
                      variant="destructive"
                      onClick={() =>
                        setRevokeTarget({ feature: row.feature, enabledOrgCount: row.enabledOrgCount })
                      }
                    >
                      Revoke all
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <Dialog open={!!revokeTarget} onOpenChange={(open) => !open && setRevokeTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Revoke {revokeTarget ? featureLabel(revokeTarget.feature) : ''}?</DialogTitle>
            <DialogDescription>
              This disables {revokeTarget?.enabledOrgCount} organization
              {revokeTarget?.enabledOrgCount === 1 ? '' : 's'} currently enabled for{' '}
              {revokeTarget ? featureLabel(revokeTarget.feature) : ''}. This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRevokeTarget(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              disabled={disableAllMutation.isPending}
              onClick={() => {
                if (!revokeTarget) return
                disableAllMutation.mutate({ feature: revokeTarget.feature })
              }}
            >
              {disableAllMutation.isPending ? 'Revoking...' : 'Revoke all'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
